import Web3Utils from 'web3-utils'
import { LSP3ProfileMetadata } from '@lukso/lsp3-contracts'

import { logWarn } from '@/shared/logger'
import { FrontendProfile } from '@/shared/models/Profile'
import { urlResolver } from './config_urls'

/**
 * hexToAssertNumber(hex: string) -> number
 *
 * equal to hextToNumber from web3-utils but will always return a number
 * and throw an exception of the number is out of bounds.
 *
 * @param hex - hex string
 */
export const hexToAssertNumber = (hex: string): number =>
  Web3Utils.hexToNumber(hex, false) as number

export const generateSalt = (): string => {
  return Web3Utils.keccak256(
    Math.random().toString(36).slice(2, 15) +
      Math.random().toString(36).slice(2, 15)
  )
}

export const linkTitles = (url: string, title: string): string =>
  title.length > 0 ? title : stripProtocol(url)

export const getFileFromUrl = async (
  url: string,
  name: string,
  defaultType = 'image/jpeg'
): Promise<File> => {
  const response = await fetch(url)
  const data = await response.blob()
  return new File([data], name, {
    type: response.headers.get('content-type') || defaultType,
  })
}

/**
 * Truncate a string by removing the middle part and inserting an ellipsis (`...`).
 *
 * @param input - The input string to be truncated.
 * @param prefixLength - The number of characters to retain from the beginning of the string. Doesn't count 0x prefix if it's present.
 * @param suffixLength - The number of characters to retain from the end of the string.
 * @returns The truncated string with an ellipsis in the middle.
 *
 * @example
 * // Returns '0x012...9'
 * truncateMiddle('0x0123456789', 3, 1);
 *
 * @example
 * // Returns 'Hello...ld!'
 * truncateMiddle('Hello, World!', 5, 3);
 *
 * @example
 * // Returns 'abcdef'
 * truncateMiddle('abcdef', 4, 3);
 */
export const truncateMiddle = (
  input?: string | null,
  prefixLength = 6,
  suffixLength?: number
): string => {
  if (!input) {
    return ''
  }

  let truncatedValue = ''
  suffixLength = suffixLength || prefixLength

  /// +3 characters accounting for ellipsis `...`
  if (input.length < prefixLength + suffixLength + 3) {
    return input
  }

  const offset = input.toLowerCase().startsWith('0x') ? 2 : 0

  truncatedValue =
    input.length > prefixLength + offset
      ? `${input.slice(0, prefixLength + offset)}...${input.slice(
          -suffixLength
        )}`
      : input

  return truncatedValue
}

/**
 * Remove schema from URL
 * example: https://example.com will became example.com
 *
 * @param url - http address
 * @returns - address without schema
 */
export const removeSchemaFromUrl = (url?: string): string => {
  if (!url) {
    return ''
  }

  return encodeURI(url.replace(/(^\w+:|^)\/\//, ''))
}

/**
 * Modify profile image url from LSP3Profile account so it can be used in img tags and backgrounds
 *
 * @param account - LSP3Profile
 * @param variant - image variant, default is 4 which is lowest quality
 * @returns url of the profile image
 */
export const profilePictureUrl = (
  account: LSP3ProfileMetadata,
  variant = 4
): string => {
  let picture = ''

  if (account?.profileImage) {
    const profileUrl = account.profileImage[variant]?.url as string
    picture = urlResolver.resolveUrl(profileUrl)
  }

  return picture
}

/**
 * Replace dynamic {key} segment of the url. I.e http://example.com/{key}/test
 *
 * @param url - http address
 * @param key - key to replace
 * @param value - value to be injected in place of key
 * @returns url with replaced value
 */
export const urlReplace = (url: string, key: string, value: string): string => {
  const pattern = `{${key}}`

  if (!url.includes(pattern)) {
    logWarn(`[urlReplace] Missing ${pattern} key in the url`)
    return ''
  }

  return url.replace(`{${key}}`, value)
}

/**
 * Strip protocol from url (http:// https:// etc)
 *
 * @param url - http address
 */
export const stripProtocol = (url: string): string => {
  return url.replace(/(^\w+:|^)\/\//, '')
}

/**
 * Generate human readable title, ie. www.lUksO.com => LUKSO
 *
 * @param title - unformatted title
 */
export const formatTitle = (title: string): string => {
  let newTitle = title.replace('www.', '').split('.')[0].toLowerCase()
  newTitle = newTitle.toUpperCase()
  return newTitle
}

/**
 * Generate short name (abbreviation) for apps
 * If title is more than one word, grab first letter of first two or three words
 * If title is one word, grab first three letters
 * ie. Universal Profile => UP or LUKSO Web App => LWA or LUKSO => LUK
 *
 * @param title - unformatted title
 */
export const formatShortName = (title: string): string => {
  let newTitle = title
  if (newTitle.slice(0, 2) === '0x') return newTitle.slice(2, 5)
  const splitTitle = title.split(/\s/)
  if (splitTitle.length > 1)
    newTitle = title
      .split(/\s/)
      .map(word => word.slice(0, 1))
      .join('')
  return newTitle.slice(0, 3).toUpperCase()
}

/**
 * Gets a timestamp in milliseconds
 *
 * @param timestamp - timestamp input
 * @returns unix timestamp
 */
export const getTimestamp: (
  timestamp: number | string
) => number = timestamp => {
  const createdAt =
    typeof timestamp === 'string' ? Number.parseInt(timestamp) : timestamp
  return createdAt * 1000
}

/**
 * Show profile name based on name and address
 *
 * @param FrontendProfile profile
 * @returns formatted profile name or address
 */
export const getProfileName = (
  profile: FrontendProfile,
  truncateNameLength = 10,
  truncateAddressLength = 4
): string => {
  const name = profile?.name
  const address = profile?.address
  if (name && address) {
    const truncateName =
      name.length > truncateNameLength
        ? `${name.slice(0, truncateNameLength)}...`
        : name
    return `@${truncateName}#${address?.slice(2, 6)}`
  } else if (address) {
    return truncateMiddle(address, truncateAddressLength, truncateAddressLength)
  } else if (name) {
    return `@${name}`
  } else {
    return ''
  }
}

export const getErrorStatusCode = (error?: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return
  // HTTP requests may return status code as `status` or as `statusCode` field. We are checking both.
  const statusCode = ((error as { status: number }).status ||
    (error as { statusCode: number }).statusCode) as number
  return statusCode === undefined && error instanceof Error
    ? getErrorStatusCode(error.cause)
    : statusCode
}

/**
 * Async version of Array.some
 * @param array
 * @param predicate
 * @returns boolean
 */
export const asyncSome = async <T>(
  array: T[],
  predicate: (item: T) => Promise<boolean>
) => {
  for (const e of array) {
    if (await predicate(e)) return true
  }
  return false
}
